if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.onViewerScroll = () => {
          if (this.ignoreViewerScroll || this._nraDragging) return;
          clearTimeout(this.thumbSyncTimer);
          this.thumbSyncTimer = setTimeout(() => this.syncThumbnailsFromViewer(), 120);
        };
        this.elements.viewer.slider?.addEventListener('scroll', this.onViewerScroll, { passive: true });
        this.elements.viewer.slider?.addEventListener('scrollend', () => {
          if (this.ignoreViewerScroll || this._nraDragging) return;
          clearTimeout(this.thumbSyncTimer);
          this.syncThumbnailsFromViewer();
        });

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.closest('.nra-pdp')) {
          this.initNraPdpGallery();
        }
      }

      initNraPdpGallery() {
        this.bindNraThumbnailNav();
        this.bindNraGalleryDrag();
      }

      bindNraThumbnailNav() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              const control = this.elements.viewer.querySelector(`button[name="${button.name}"]`);
              control?.click();
            },
            true
          );
        });
      }

      bindNraGalleryDrag() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;

        let isDragging = false;
        let startX = 0;
        let startScroll = 0;
        let activePointer = null;

        slider.classList.add('nra-gallery-draggable');

        const snapToNearest = () => {
          const viewer = this.elements.viewer;
          const index = viewer.currentIndex();
          const item = viewer.sliderItemsToShow?.[index];
          if (!item) return;
          slider.scrollTo({ left: item.offsetLeft, behavior: 'auto' });
          this.syncThumbnailsFromViewer();
        };

        const stopDrag = (event) => {
          if (!isDragging) return;
          isDragging = false;
          this._nraDragging = false;
          activePointer = null;
          slider.classList.remove('is-dragging');
          if (event?.pointerId != null) {
            try {
              slider.releasePointerCapture(event.pointerId);
            } catch (error) {
              /* ignore */
            }
          }
          snapToNearest();
        };

        slider.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            isDragging = true;
            this._nraDragging = true;
            activePointer = event.pointerId;
            startX = event.clientX;
            startScroll = slider.scrollLeft;
            slider.classList.add('is-dragging');
            slider.setPointerCapture(event.pointerId);
            event.preventDefault();
          },
          { passive: false }
        );

        slider.addEventListener(
          'pointermove',
          (event) => {
            if (!isDragging || event.pointerId !== activePointer) return;
            event.preventDefault();
            slider.scrollLeft = startScroll - (event.clientX - startX);
          },
          { passive: false }
        );

        slider.addEventListener('pointerup', stopDrag);
        slider.addEventListener('pointercancel', stopDrag);
      }

      syncThumbnailsFromViewer() {
        if (!this.elements.thumbnails || !this.elements.viewer?.currentIndex) return;
        const items = this.elements.viewer.sliderItemsToShow || [];
        const index = this.elements.viewer.currentIndex();
        const current = items[index];
        if (!current) return;
        this.onSlideChanged({
          detail: {
            currentPage: index + 1,
            currentElement: current,
          },
        });
      }

      onSlideChanged(event) {
        if (this.ignoreViewerScroll || this._nraDragging) return;
        if (!this.elements.thumbnails) return;
        const current = event.detail?.currentElement;
        let thumbnail = null;
        if (current?.dataset?.mediaId) {
          thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        }
        if (!thumbnail && event.detail?.currentPage) {
          thumbnail = this.elements.thumbnails.querySelectorAll('[data-target]')[event.detail.currentPage - 1];
        }
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.ignoreViewerScroll = true;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) {
            this.ignoreViewerScroll = false;
            return;
          }
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
          this.ignoreViewerScroll = false;
        }, 80);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        const button = thumbnail.querySelector('button') || thumbnail;
        const current = this.elements.thumbnails.querySelector('[aria-current="true"]');
        if (current === button) return;
        if (current) current.removeAttribute('aria-current');
        button.setAttribute('aria-current', 'true');
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
